/* eslint-disable no-console */
'use client';

import { AppPage } from '@/components/AppPage';
import { routes } from '@/routes';

import type {
  FormattedCart,
  FormattedCartItem,
  PGCandidateOrderEntity,
  PGCandidateOrderGroupedItem,
  PGCorpCandidateOrderItem,
} from '@wolfejs/ms-api-js/src/types/orders';
import { usePGCorpCandidateOrder } from '@wolfejs/ms-api-react/src/hooks/pgcorp/cart';
import CartItem from '@wolfejs/ms-ui/Cart/CartItem/CartItem';
import { OrderSummary } from '@wolfejs/ms-ui/Cart/OrderSummary';
import { Alert } from '@wolfejs/ui/Alert';
import { Title } from '@wolfejs/ui/Title';
import Cookies from 'js-cookie';
import { useRouter } from 'next/navigation';
import React, { useEffect } from 'react';
import { EmptyCart } from './EmptyCart';
import { deleteLineItem } from '@/utils/cart';
import { usePGCorpFundsBank } from '@wolfejs/ms-api-react/src/hooks/pgcorp/fundsBank';


export const getProductDetailsFromId = (id: string, cartData: PGCandidateOrderEntity) => {
  const notFoundResponse = { name: '', image: '' };
  if (!cartData) {
    return notFoundResponse;
  }
  const cartItems = cartData.items;
  const foundItem = cartItems.find((itm: PGCorpCandidateOrderItem) => itm.product === id);

  const retVals = {
    name: foundItem?.product_data?.product.brand.name as string,
    image: foundItem?.product_data?.product.brand.image_url as string,
  };
  return retVals;
};

export const formatCartData = (cartData?: PGCandidateOrderEntity) => {
  if (!cartData) return null;
  const groupedItems = cartData.grouped_items as PGCandidateOrderGroupedItem[];

  const itemsArray: FormattedCartItem[] = [];
  let totalDiscount = 0;
  let subTotal = 0;
  groupedItems.map((grItm, index) => {
    const productDetails = getProductDetailsFromId(grItm.product, cartData);
    subTotal += grItm.denomination * grItm.quantity;
    totalDiscount += grItm.denomination * grItm.quantity - grItm.total_cost;
    const newItem = {
      total: grItm.total_cost,
      title: productDetails.name,
      quantity: grItm.quantity,
      unit_price: grItm.denomination,
      thumbnail: productDetails.image,
      id: index.toString(),
      productId: grItm.product,
    };
    itemsArray.push(newItem);
  });
  const newCart = {
    items: itemsArray,
    tax_total: 0,
    discount_total: totalDiscount,
    subtotal: subTotal,
  };
  return newCart;
};

export type ShoppingCartPageContainerProps = {
  slug: string;
};

export function ShoppingCartPageContainer({ slug }: ShoppingCartPageContainerProps) {
  const router = useRouter();
  const [candidateCart, setCandidateCart] = React.useState<FormattedCart | null>();
  const [_removingFromCart, setRemovingFromCart] = React.useState(false);

  // Fallback to fetching cart client-side if server fails for some reason & a cart cookie is set
  const cartId = Cookies.get('_pgcorp_cart_uuid');
  const { data: cartData, isLoading, error: errorGettingCart } = usePGCorpCandidateOrder();
  const { data: fundsBank, isLoading: loadingFundsBank } = usePGCorpFundsBank();
  const handleRemoveFromCart = async (lineItemId: string) => {
    setRemovingFromCart(true);

    if (!fundsBank?.uuid) {
      console.error('No funds bank uuid found');
      return;
    }

    const theLineItem = candidateCart?.items[parseInt(lineItemId)] as {
      total: number;
      title: string;
      quantity: number;
      unit_price: number;
      thumbnail: string;
      id: string;
      productId: string;
    };
    if (!theLineItem) {
      console.error('lineItem not found to remove');
      return;
    }
    try {
      const updatedCart = await deleteLineItem(
        theLineItem.productId,
        theLineItem.quantity,
        theLineItem.unit_price,
        fundsBank.uuid
      );
      if (!updatedCart.uuid) {
        setCandidateCart(null);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setRemovingFromCart(false);
    }
  };

  useEffect(() => {
    if (cartData && cartData.grouped_items && Array.isArray(cartData.grouped_items)) {
      const formattedCartData = formatCartData(cartData);
      if (formattedCartData) {
        setCandidateCart(formattedCartData);
      }
      return;
    }
    setCandidateCart(null);
  }, [cartData]);

  return (
    <AppPage className="p-4">
      <div className="flex justify-between gap-2">
        <Title className="mb-0">Shopping Cart</Title>
      </div>
      {cartId && isLoading ? <>Loading...</> : null}
      {errorGettingCart ? (
        <Alert color="error">There was an error getting your cart. Please refresh the page to try again.</Alert>
      ) : !cartId ? (
        <EmptyCart
          onContinueShopping={() => {
            router.push(`/${slug}${routes.products}`);
          }}
        />
      ) : (
        <>
          <div className="grid gap-6 @lg:grid-cols-3">
            <div className="@lg:col-span-2">
              {candidateCart?.items.map((lineItem, index) => (
                <CartItem
                  className="mb-4"
                  key={lineItem.id}
                  cartItem={lineItem as FormattedCartItem}
                  onDelete={handleRemoveFromCart}
                  index={index}
                />
              ))}
            </div>
            <div className="@lg:col-span-1">
              <div className="mb-4 rounded-lg border border-default-100 bg-white p-6 @container">
                <div className="flex justify-between">
                  <Title as="h3">Order summary</Title>
                </div>
                <OrderSummary
                  cart={candidateCart}
                  buttonText="Proceed to Checkout"
                  buttonOnClick={() => {
                    router.push(`/${slug}${routes.checkout}`);
                  }}
                  linkText="Continue Shopping"
                  linkOnClick={() => {
                    router.push(`/${slug}${routes.products}`);
                  }}
                />
              </div>
              {/* leaving this in here for now for a starting point if we do add promo codes */}
              {/* <div className="rounded-lg border border-default-100 bg-white p-6 @container">
                <p className="mb-2">Do you have a promo code?</p>
                <PromoCodeInput
                  isLoading={applyingPromoCode}
                  value={promoCode}
                  onChange={e => {
                    setPromoCode(e.target.value);
                  }}
                  onSubmit={handlePromoCodeSubmit}
                />
                {promoCodeError && <p className="mt-2 text-error-600">{promoCodeError}</p>}
                {!isEmpty(candidateCart?.discounts) && (
                  <Button className="mt-2 w-full" color={UIColors.error} onClick={handleRemovePromoCode}>
                    Delete promo
                  </Button>
                )}
              </div> */}
            </div>
          </div>
        </>
      )}
    </AppPage>
  );
}
